import { type PaletteKey, type PixelCanvas, sprite } from '../../../lib';
import { box, byRow, geometry, outlined } from '../effect-guns.sprite';

/**
 * Jack-in-the-Box (the Jester set's gun, ADR-0160 amended): a wind-up toy blaster. A toy box striped red and yellow in a
 * purple frame, its crank turning on its side; out of its front a spring, and on the spring a little clown's head at the
 * muzzle (a red nose where the shots come out, a purple hat with a pompom), bobbing; on every shot the head is yanked
 * back into the box and springs out again.
 */

/** The clown at the end of the spring: its middle, its half-width and half-height (squashed and stretched). */
interface Clown {
  x: number;
  y: number;
  rx: number;
  ry: number;
}

const REST: Clown = { x: 58, y: 17.5, rx: 4.6, ry: 4.6 };

/** The spring from the box's front (x 46) to the clown's chin: coils of steel, lit strands in front, dark ones behind. */
function spring(c: PixelCanvas, to: number, cy: number, half: number): void {
  const len = Math.max(2, to - 46);
  const turns = Math.max(2, Math.round(len / 3));
  const pitch = len / turns;
  for (let k = 0; k < turns; k++) {
    const x0 = 46 + k * pitch;
    // The strand behind: up from the foot of this turn to the top of the next.
    c.line(Math.round(x0 + pitch * 0.5), Math.round(cy - half), Math.round(x0 + pitch), Math.round(cy + half), '2');
    // The strand in front: lit, a glint near its top.
    c.line(Math.round(x0), Math.round(cy + half), Math.round(x0 + pitch * 0.5), Math.round(cy - half), '5');
    c.set(Math.round(x0 + pitch * 0.4), Math.round(cy - half + 1), 'w');
  }
}

/** The clown's head: a white face lit from the top-left, orange hair at the back, a purple hat, a red nose. */
function clown(c: PixelCanvas, { x, y, rx, ry }: Clown): void {
  // Hair tufts behind the face.
  for (const [hx, hy, r] of [
    [x - rx * 0.8, y - ry * 0.55, 2],
    [x - rx * 0.95, y + ry * 0.1, 1.9],
    [x - rx * 0.55, y - ry * 0.9, 1.8],
  ] as const)
    for (let py = Math.floor(hy - r); py <= hy + r; py++)
      for (let px = Math.floor(hx - r); px <= hx + r; px++)
        if (Math.hypot(px + 0.5 - hx, py + 0.5 - hy) < r) c.set(px, py, px + py < hx + hy - 0.5 ? 'o' : 'u');
  for (let py = Math.floor(y - ry); py <= y + ry; py++)
    for (let px = Math.floor(x - rx); px <= x + rx; px++) {
      const [dx, dy] = [(px + 0.5 - x) / rx, (py + 0.5 - y) / ry];
      const r = dx * dx + dy * dy;
      if (r > 1) continue;
      const l = -dx * 0.7 - dy * 0.7;
      c.set(px, py, r > 0.6 && l < -0.25 ? '4' : l < -0.35 || (r > 0.55 && l < 0.1) ? '5' : 'w');
    }
  // Its eyes, its smile, its red nose (the muzzle).
  const [ex, ey] = [Math.round(x + rx * 0.2), Math.round(y - ry * 0.3)];
  c.set(ex, ey, 'k').set(ex + 2, ey, 'k');
  c.set(ex - 1, ey + 3, 'R')
    .set(ex, ey + 4, 'R')
    .set(ex + 1, ey + 4, 'R')
    .set(ex + 2, ey + 3, 'R');
  const nx = Math.round(x + rx) - 1;
  const ny = Math.round(y + ry * 0.15);
  c.set(nx - 1, ny, 'r')
    .set(nx, ny, 'r')
    .set(nx - 1, ny + 1, 'R')
    .set(nx, ny + 1, 'r')
    .set(nx - 1, ny - 1, 'i');
  c.set(nx + 1, ny, 'r').set(nx + 1, ny + 1, 'R');
  // A purple hat on top, cocked back, a yellow pompom.
  const [hx, hy] = [Math.round(x - 0.5), Math.round(y - ry)];
  for (let k = 0; k < 4; k++) {
    const w = 3 - Math.floor(k * 0.8);
    for (let i = 0; i < w; i++) c.set(hx - k + i - 1, hy - k, i === 0 ? 'p' : 'P');
  }
  c.set(hx - 5, hy - 4, 'y')
    .set(hx - 4, hy - 4, 'Y')
    .set(hx - 5, hy - 5, 'y');
}

/** The crank's arm from its hub on the box's side at quarter turn `q` (0 up, 1 forward, 2 down, 3 back). */
const CRANK: readonly (readonly [number, number])[] = [
  [0, -1],
  [1, 0],
  [0, 1],
  [-1, 0],
];

const jackbox = (q: number, at: Clown, coil = 2.6) =>
  outlined((c) => {
    // The stock: purple, striped yellow like a stick of candy, a gold cap.
    box(
      c,
      5,
      16,
      22,
      25,
      [2, 1],
      (x, y) => {
        const stripe = (x + y) % 6 < 2;
        return (
          stripe ? (y === 16 ? 'y' : y >= 24 ? 'U' : 'Y') : byRow(16, 25, ['P', 'P', 'p', 'i'], 1, 1)(x, y)
        ) as PaletteKey;
      },
      'l',
    );
    box(c, 5, 17, 7, 24, [1], (_x, y) => (y < 19 ? 'y' : y > 22 ? 'U' : 'Y'), 'l');
    // The toy box: a purple frame, lit along its top and left, round a panel striped red and yellow; its lid on top.
    box(c, 21, 9, 45, 27, [1], (x, y) => {
      const frame = x <= 22 || x >= 44 || y <= 10 || y >= 26;
      if (frame) return x === 21 || y === 9 ? 'p' : 'P';
      const red = Math.floor((x - 23) / 3) % 2 === 0;
      const lit = y === 11 || x === 23;
      const shade = y >= 24 || x >= 42;
      return red ? (lit ? 'i' : shade ? 'R' : 'r') : lit ? 'y' : shade ? 'U' : 'Y';
    });
    box(c, 20, 6, 46, 8, [1], (_x, y) => (y === 6 ? 'i' : y === 7 ? 'p' : 'P'));
    // The crank on its side: a steel hub, an iron arm, a white knob.
    const [dx, dy] = CRANK[q % 4] as readonly [number, number];
    const [hx, hy] = [33, 17];
    for (let k = 1; k <= 5; k++) {
      c.set(hx + dx * k, hy + dy * k, '1');
      c.set(hx + dx * k + (dy === 0 ? 0 : 1), hy + dy * k + (dx === 0 ? 0 : 1), '2');
    }
    const [kx, ky] = [hx + dx * 6, hy + dy * 6];
    c.rect(kx - 1, ky - 1, 3, 3, '5');
    c.set(kx - 1, ky - 1, 'w')
      .set(kx, ky - 1, 'w')
      .set(kx - 1, ky, 'w')
      .set(kx + 1, ky + 1, '4');
    c.rect(hx - 1, hy - 1, 3, 3, '4');
    c.set(hx - 1, hy - 1, '5').set(hx, hy, 'k');
    // The trigger.
    box(c, 30, 28, 33, 30, [0, 1], (_x, y) => (y === 28 ? 'Y' : 'U'), 'r');
    // The spring, and the clown on it.
    spring(c, Math.round(at.x - at.rx) + 1, at.y + 1.5, coil);
    clown(c, at);
  });

/** The clown bobbing on its spring, frame by frame. */
const BOB = [0, -1, 0, 1] as const;
const idle = (q: number) => jackbox(q, { ...REST, y: REST.y + (BOB[q] ?? 0) });

export default sprite({
  name: 'pollinator_jester',
  ...geometry,
  notes: `Jack-in-the-Box (ADR-0160 amended, the Jester set's gun): a wind-up toy blaster. A toy box striped red and yellow
in a purple frame, a crank on its side turning a quarter each idle frame (a white knob on an iron arm round a steel hub),
a purple stock striped yellow like candy; out of its front a steel spring (lit strands in front, dark ones behind) and on
it a clown's head at the muzzle (white, orange hair, a purple hat with a pompom, a red nose: the shots come out of it),
bobbing a px up and down. Fire: the head yanked back into the box, sprung out squashed, stretched tall, at rest. Its
flair: twinkles round the head, a puff of confetti now and then. No team keys. Rubric (4×): S4 R5 P4 L4 C4 A4 F4.
Weakest: the box is the chunkiest body of the mythic guns (20 rows with its lid); the crank's arm is iron on stripes, its
white knob carries it at 1:1.`,
  anims: {
    idle: { fps: 6, frames: [idle(0), idle(1), idle(2), idle(3)] },
    fire: {
      fps: 24,
      loop: false,
      frames: [
        jackbox(0, { x: 52, y: 17.5, rx: 4, ry: 4.8 }, 3.2),
        jackbox(0, { x: 58, y: 18, rx: 5, ry: 3.8 }, 1.8),
        jackbox(0, { x: 58, y: 16.5, rx: 4.2, ry: 5.2 }, 2.4),
        idle(0),
      ],
    },
  },
});
